import ServiceCard from '@/components/ServiceCard';
import { motion } from 'framer-motion';

const Services = () => {
  const services = [
    {
      title: "Documentaries",
      description: "At SAMFREN OJO MEDIA, our documentaries spotlight real lives, untold stories, and marginalized voices across Africa. We bring depth, emotion, and authenticity to every story — from personal journeys to social impact narratives — capturing the beauty and complexity of human experience.",
      images: [
        "/documentaries/documentary-1.jpeg",
        "/documentaries/documentary-2.jpeg",
        "/documentaries/documentary-3.jpeg"
      ],
      features: [
        "In-depth research and development",
        "Cinematic storytelling",
        "Interview coordination",
        "Location scouting",
        "Post-production and editing"
      ]
    },
    {
      title: "Events",
      description: "We cover weddings, conferences, NGO programs, and other events with creative precision and storytelling flair. We document the moments that deserve to live forever.",
      images: [
        "/events/events-1.jpeg",
        "/events/events-2.jpeg",
        "/events/events-3.jpeg",
        "/events/events-4.jpeg",
        "/events/events-5.jpeg",
        "/events/events-6.jpeg"
      ],
      features: [
        "Event coverage",
        "Multi-camera setup",
        "Live streaming",
        "Highlight reels",
        "Corporate interviews"
      ]
    },
    {
      title: "Music Videos",
      description: "We create original music videos that match your sound with cinematic visuals. From the concept to execution, we bring your song to life with storytelling and crisp editing that feels global.",
      images: [
        "/music video/music-1.jpeg",
        "/music video/music-2.jpeg",
        "/music video/music-3.jpeg",
        "/music video/music-4.jpeg",
        "/music video/music-5.jpeg"
      ],
      features: [
        "Concept development",
        "Location scouting",
        "Choreography coordination",
        "Professional lighting and sound",
        "Color grading and post-production"
      ]
    },
    {
      title: "Real Estate",
      description: "We help developers, agents, and property owners showcase their listings with visually compelling property videos. From architectural shots to lifestyle edits, we turn your spaces into visual experiences buyers can’t ignore.",
      images: [
        "/real estate/real-estate-1.jpeg",
        "/real estate/real-estate-2.jpeg",
        "/real estate/real-estate-3.jpeg",
        "/real estate/real-estate-4.jpeg",
        "/real estate/real-estate-5.jpeg"
      ],
      features: [
        "Aerial drone footage",
        "Virtual tours",
        "Twilight photography",
        "Floor plan visualization",
        "Professional editing"
      ]
    },
    {
      title: "Weddings",
      description:
        "We capture the magic of your special day with cinematic wedding films and stunning photography. From the heartfelt vows to the lively celebrations, we ensure every precious moment is beautifully preserved for a lifetime.",
      images: [
        "/weddings/weddings-1.jpeg",
        "/weddings/weddings-2.jpeg",
        "/weddings/weddings-3.PNG",
        "/weddings/weddings-4.jpeg",
        "/weddings/weddings-5.jpeg",
        "/weddings/weddings-6.jpeg",
        "/weddings/weddings-7.jpeg",
        "/weddings/weddings-8.jpeg",
      ],
      features: [
        "Full-day coverage",
        "Cinematic highlight films",
        "Drone shots for breathtaking views",
        "Pre-wedding shoots",
        "Customizable wedding albums"
      ]

    },
    {
      title: "Fashion",
      description: "We transform raw footage into polished, professional content. Whether you need basic editing or complex visual effects, our team of experienced editors will ensure your project meets the highest standards of quality.",
      images: [
        "/fashion/fashion-1.jpeg",
        "/fashion/fashion-2.jpeg",
        "/fashion/fashion-3.jpeg"
      ],
      features: [
        "Video editing",
        "Color correction & grading",
        "Sound design & mixing",
        "Visual effects",
        "Motion graphics"
      ]
    }
  ];


  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Our <span className="text-burnt-orange">Services</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive media production services tailored to bring your vision to life with creativity and precision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-dark-gray">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              return <ServiceCard key={index} service={service} index={index} />
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-dark-gray/80">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Process</h2>
            <div className="w-20 h-1 bg-burnt-orange mx-auto"></div>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              We follow a streamlined process to ensure your project is completed on time, on budget, and exceeds your expectations.
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gray-700 -translate-y-1/2"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4">
              {[{
                number: "01",
                title: "Discovery",
                description: "We start by understanding your vision, goals, and requirements."
              }, {
                number: "02",
                title: "Planning",
                description: "Our team creates a detailed plan and timeline for your project."
              }, {
                number: "03",
                title: "Production",
                description: "We capture high-quality footage with professional equipment."
              }, {
                number: "04",
                title: "Delivery",
                description: "Final touches are added, and your project is delivered on time."
              }].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="relative bg-gray-900/50 p-6 rounded-lg border border-gray-800 text-center transform hover:scale-105 hover:shadow-xl transition-all duration-300 ease-in-out"
                >
                  {/* Icon with Hover Effect */}
                  <div className="w-16 h-16 rounded-full bg-burnt-orange/10 border-2 border-burnt-orange flex items-center justify-center text-2xl font-bold text-burnt-orange mb-4 mx-auto transform group-hover:scale-110 transition-transform duration-300 ease-in-out">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-dark-gray to-gray-900">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how we can bring your vision to life. Contact us today for a free consultation.
            </p>
            <a
              href="/contact"
              className="inline-block bg-burnt-orange hover:bg-burnt-orange/90 text-white font-semibold py-3 px-8 rounded-md transition-colors"
            >
              Get Started
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
